import ModeIcon from '@mui/icons-material/Mode';
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';

const ColorOption = ({ color }: { color: string }) => {
  return (
    <Box
      height={16}
      width={16}
      borderRadius={'50%'}
      bgcolor={color}
      border={'1px solid rgba(15, 23, 42, 0.2)'}
      sx={{ flex: '0 0 16px' }}
    />
  );
};

type Props = {
  colors: string[];
  drawMode: (color: string) => void;
  endDrawMode: () => void;
  setPenSize: (size: number) => void;
};

const Pencils = ({ colors, drawMode, endDrawMode, setPenSize }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedColor, setSelectedColor] = useState<string>('#64748b');
  // const [inDrawMode, setInDrawMode] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorClick = (color: string) => {
    setSelectedColor(color);
    // setInDrawMode(true);
    drawMode(color);
  };

  const cancelDrawMode = () => {
    endDrawMode();
    // setInDrawMode(false)
    setSelectedColor('#64748b');
  };

  const lastTap = useRef<number>(0);
  // this captures whether it is a single click or double
  const handlePencilClick = (e: React.MouseEvent<HTMLElement>) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // Adjust this value as needed

    if (now - lastTap.current < DOUBLE_TAP_DELAY) {
      handleClick(e);
      lastTap.current = 0;
    } else {
      lastTap.current = now;
      cancelDrawMode();
    }
  };

  // this function is for adjusting the size of the pen
  const handlePenSizeClick = (size: number) => {
    setPenSize(size);
    handleClose();
  };

  return (
    <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={1} height={'100%'} px={1.5}>
      <Tooltip title={'Single click exits draw mode. Double click opens pen size.'} arrow>
        <Box
          onClick={handlePencilClick}
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          sx={{ cursor: 'pointer' }}
        >
          <Box
            display={'grid'}
            sx={{
              placeItems: 'center',
              width: 38,
              height: 38,
              borderRadius: 2,
              border: '1px solid #d7dde7',
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#eef2f7' },
            }}
          >
            <ModeIcon fontSize='small' htmlColor={selectedColor} />
          </Box>
          <Typography fontSize={10} color={'#475569'} mt={0.25}>
            Draw
          </Typography>
        </Box>
      </Tooltip>
      <Box display={'flex'} alignItems={'center'} gap={0.5} px={1} py={0.75} borderRadius={2} bgcolor={'#f8fafc'}>
        {colors.map((color) => {
          return (
            <Box
              key={color}
              onClick={() => handleColorClick(color)}
              display={'grid'}
              sx={{
                placeItems: 'center',
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: selectedColor === color ? '2px solid #1e293b' : '2px solid transparent',
                cursor: 'pointer',
                bgcolor: '#ffffff',
              }}
            >
              <ColorOption color={color} />
            </Box>
          );
        })}
      </Box>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={() => handlePenSizeClick(1)}>Small</MenuItem>
        <MenuItem onClick={() => handlePenSizeClick(2)}>Medium</MenuItem>
        <MenuItem onClick={() => handlePenSizeClick(3)}>Large</MenuItem>
      </Menu>
    </Box>
  );
};

export default Pencils;
